import type { UploadTaskRecord } from '@/api/modules/material/uploadTask.types';
import type { UploadTask, UploadTaskFile, UploadTaskProgress } from './types';

/**
 * 上传任务的「本地态 ↔ 服务端态」合并与断点对账。
 *
 * 背景：任务创建时先调服务端接口拿 taskId，但此时服务端只知道「有这么个任务」，
 * 计数与素材明细都是空的；上传过程中真正准确的数据在前端内存里。因此：
 * - 列表展示要把「服务端分页结果」与「本地会话内的实时数据」合并；
 * - 浏览器被强关 / 关机导致没来得及回写服务端时，要靠本地持久化数据补一次对账。
 *
 * 本文件全部是纯函数，方便单测。
 */

/** 内存里是否还持有文件句柄（刷新后 `file` 会被持久化层剥离） */
export const isTaskLive = (task: UploadTask): boolean =>
  task.files.some((file) => Boolean(file.file));

/** 可入库的素材：上传成功且拿到 OSS 路径 */
export const collectSubmittableFiles = (task: UploadTask): UploadTaskFile[] =>
  task.files.filter((file) => file.status === 'success' && Boolean(file.info?.ossKey));

/** 非成功的素材（失败 + 取消），回写服务端时统一标记为失败 */
export const collectFailedFiles = (task: UploadTask): UploadTaskFile[] =>
  task.files.filter((file) => file.status !== 'success');

/** 刷新后的对账动作 */
export type ReconcileAction =
  | { type: 'skip'; taskId: string; reason: 'synced' | 'live' | 'empty' }
  | {
      type: 'submit';
      taskId: string;
      submittable: UploadTaskFile[];
      failed: UploadTaskFile[];
    }
  | { type: 'fail-all'; taskId: string; files: UploadTaskFile[] };

/**
 * 计算每个本地任务的补偿动作。
 *
 * 判定顺序（先短路，避免误操作）：
 * 1. 已成功回写服务端 → 跳过（幂等，防止重复入库）；
 * 2. 内存里还持有文件句柄（当前会话正在管理它）→ 跳过；
 *    对账只服务于「会话已被重置、File 句柄丢失」的中断任务，
 *    当前会话的失败/重试由页面上的「重试」入口处理，不能在这里抢跑；
 * 3. 没有任何文件 → 跳过；
 * 4. 有成功素材 → 补一次入库；
 * 5. 否则 → 会话已重置且没有可入库的内容，无法续传，全部标记失败。
 */
export const buildReconcilePlan = (tasks: UploadTask[]): ReconcileAction[] =>
  tasks.map((task) => {
    if (task.serverSynced) {
      return { type: 'skip', taskId: task.taskId, reason: 'synced' } as const;
    }
    if (isTaskLive(task)) {
      return { type: 'skip', taskId: task.taskId, reason: 'live' } as const;
    }
    if (task.files.length === 0) {
      return { type: 'skip', taskId: task.taskId, reason: 'empty' } as const;
    }

    const submittable = collectSubmittableFiles(task);
    if (submittable.length > 0) {
      return {
        type: 'submit',
        taskId: task.taskId,
        submittable,
        failed: collectFailedFiles(task),
      } as const;
    }

    return { type: 'fail-all', taskId: task.taskId, files: task.files } as const;
  });

/** 任务的「重试」可用性 */
export interface TaskRetryState {
  /** 是否可点「重试」 */
  canRetry: boolean;
  /** 可重新上传的失败素材 uid（本地仍有 File 且标记为可重试） */
  retryableUploadUids: string[];
  /** 是否需要「只重试入库」（上传都成功，但入库没成功） */
  canRetrySubmit: boolean;
}

/**
 * 单个文件能否「重新上传」。
 *
 * 三个条件缺一不可：
 * 1. 处于失败态；
 * 2. 构造时被标记为可重试（前置校验失败项为 false，重试无意义）；
 * 3. **当前会话仍持有 `File` 句柄** —— `retryable` 会跟着持久化数据一起恢复成 true，
 *    但刷新后 `File` 已被剥离，此时点重试不会发生任何事，必须按不可重试处理。
 *
 * 因此「生成上传路径接口超时」这类**本次会话内**的失败天然可重试：
 * 失败时 `retryable` 仍为 true、`File` 也还在内存里，重试会重新走一遍
 * 「申请 OSS 路径 → 上传」的完整流程（`generatedFileName` 为空即重新申请）。
 */
export const isFileRetryable = (file: {
  status: UploadTaskFile['status'];
  retryable?: boolean;
  file?: File;
}): boolean => file.status === 'error' && !!file.retryable && !!file.file;

/**
 * 「重试」按钮的可用性判定：
 * - 上传失败但本地仍持有文件 → 可重传（成功后自动补入库）；
 * - 上传都成功、只是入库失败 → 可只重试入库；
 * - 非当前会话（`File` 句柄已丢失）/ 失败项不可重传 → 禁用。
 *
 * ⚠️ **不能用 `serverSynced` 一票否决**：任务在上传结束时就可能已经回写过服务端
 * （例如整批文件都因「生成上传路径」超时而失败时，`submitTask` 会立刻回写全失败）。
 * 此时文件句柄还在内存里，属于「当前会话可重传」，仍必须给出重试入口。
 * `serverSynced` 只用于对账幂等（见 `buildReconcilePlan`），不参与重试判定。
 */
export const getTaskRetryState = (task: UploadTask | undefined): TaskRetryState => {
  // 重试必须能拿到文件本体：会话已重置（刷新过）的任务一律禁用
  if (!task || !isTaskLive(task)) {
    return { canRetry: false, retryableUploadUids: [], canRetrySubmit: false };
  }

  const retryableUploadUids = task.files.filter(isFileRetryable).map((file) => file.uid);

  // 只有「没有可重传的上传失败项、且上传都成功但入库没成功」才走纯入库重试
  const canRetrySubmit =
    retryableUploadUids.length === 0 &&
    task.status === 'failed' &&
    !task.serverSynced &&
    collectSubmittableFiles(task).length > 0;

  return {
    canRetry: retryableUploadUids.length > 0 || canRetrySubmit,
    retryableUploadUids,
    canRetrySubmit,
  };
};

/** 列表行 = 服务端记录 + 当前会话的本地实时数据 */
export interface UploadTaskRow extends UploadTaskRecord {
  local?: {
    /** 本地实时进度（服务端在任务结束前计数是 0） */
    progress: UploadTaskProgress;
    /** 「重试」是否可用 */
    canRetry: boolean;
    /** 入库失败原因 */
    submitError?: string;
  };
}

/**
 * 合并服务端分页记录与本地任务。
 * 保持服务端返回的顺序（分页/排序由后端决定），只做字段级补齐。
 */
export const buildTaskRows = (
  records: UploadTaskRecord[],
  localTasks: UploadTask[]
): UploadTaskRow[] => {
  const localMap = new Map(localTasks.map((task) => [task.taskId, task]));

  return records.map((record) => {
    const local = localMap.get(record.taskId);
    /**
     * 只有**当前会话还持有文件句柄**的任务才用本地影子数据覆盖服务端计数。
     *
     * 刷新 / 关页后 `File` 已被持久化层剥离，本地那份进度会永远停在「上传中」的最后一帧
     * （例如 12 个文件里最后那个卡在 uploading，本地算出来是「成功 11 失败 0」），
     * 与服务端已经对账出来的最终结果矛盾 —— 这种时候一律以服务端为准。
     */
    if (!local || !isTaskLive(local)) return { ...record };

    const retryState = getTaskRetryState(local);

    return {
      ...record,
      // 上传没结束前，服务端计数一定是 0，用本地数据覆盖才看得出进度
      totalCount: local.progress.total,
      successCount: local.progress.success,
      failCount: local.progress.failed,
      local: {
        progress: local.progress,
        canRetry: retryState.canRetry,
        submitError: local.errorMessage,
      },
    };
  });
};
